/** User info decoded from JWT session cookie/storage */
export interface JWTUser {
  username: string;
  name?: string;
  /** Alias for username (UPN/email from JWT payload) */
  email?: string;
}

/**
 * Clé sessionStorage utilisée pour stocker le session_jwt en fallback du cookie.
 * Indispensable en navigation privée Chrome : les cookies tiers (iframe cross-site)
 * y sont bloqués par défaut, donc on doit pouvoir relire le JWT depuis le storage.
 */
export const JWT_STORAGE_KEY = 'session_jwt';

/**
 * Nom de l'événement custom émis sur `window` chaque fois que le JWT change
 * (set ou clear). Permet aux composants React (via `useIsAuthenticated`) de
 * se re-render quand l'auth se termine de manière asynchrone (typiquement en
 * mode iframe, où `useIframeAuthBoot` met à jour le storage après le 1er render).
 */
const AUTH_CHANGED_EVENT = 'smartrecord:auth-changed';

function dispatchAuthChanged(): void {
  try {
    window.dispatchEvent(new Event(AUTH_CHANGED_EVENT));
  } catch (e) {
    /* ignore (SSR / vieux navigateurs) */
  }
}

/**
 * S'abonne aux changements de JWT (set/clear) via l'événement custom et
 * `storage` (changements faits depuis un autre onglet). Retourne une fonction
 * de désinscription.
 */
export function subscribeAuthChanges(cb: () => void): () => void {
  const storageHandler = (e: StorageEvent) => {
    if (e.key === null || e.key === JWT_STORAGE_KEY) cb();
  };
  window.addEventListener(AUTH_CHANGED_EVENT, cb);
  window.addEventListener('storage', storageHandler);
  return () => {
    window.removeEventListener(AUTH_CHANGED_EVENT, cb);
    window.removeEventListener('storage', storageHandler);
  };
}

/**
 * Récupère le session_jwt depuis sessionStorage en priorité (marche en navigation
 * privée Chrome), fallback sur le cookie (qui marche en navigation normale).
 * Retourne null si aucun JWT trouvé.
 */
export function getJWT(): string | null {
  try {
    const fromStorage = window.sessionStorage.getItem(JWT_STORAGE_KEY);
    if (fromStorage) return fromStorage;
  } catch (e) {
    /* sessionStorage indisponible (ex: SSR), fallback cookie */
  }
  const match = document.cookie.match(/session_jwt=([^;]+)/);
  return match ? match[1] : null;
}

/**
 * Stocke le session_jwt en sessionStorage. Doit être appelé à chaque fois que le
 * backend renvoie un nouveau JWT dans le body (ex: réponse de /auth/iframe-token).
 */
export function setJWT(jwt: string): void {
  try {
    window.sessionStorage.setItem(JWT_STORAGE_KEY, jwt);
  } catch (e) {
    /* ignore */
  }
  dispatchAuthChanged();
}

/**
 * Supprime le JWT du sessionStorage. À appeler en cas de logout / 401 définitif.
 */
export function clearJWT(): void {
  try {
    window.sessionStorage.removeItem(JWT_STORAGE_KEY);
  } catch (e) {
    /* ignore */
  }
  dispatchAuthChanged();
}

/**
 * Impersonation (vue "lecture seule" d'un autre utilisateur, réservée à l'admin
 * autorisé). On stocke la cible en sessionStorage : le mode survit à un reload de
 * l'onglet mais reste cloisonné par onglet (pas de fuite vers d'autres onglets).
 * Le JWT réel n'est JAMAIS modifié — seule l'identité applicative (via
 * `getUserFromJWT`) et l'en-tête `X-Impersonate-Email` (cf. api.ts) basculent.
 */
const IMPERSONATE_EMAIL_KEY = 'sr_impersonate_email';
const IMPERSONATE_NAME_KEY = 'sr_impersonate_name';

export interface ImpersonationTarget {
  email: string;
  name: string;
}

export function getImpersonation(): ImpersonationTarget | null {
  try {
    const email = window.sessionStorage.getItem(IMPERSONATE_EMAIL_KEY);
    if (!email) return null;
    return { email, name: window.sessionStorage.getItem(IMPERSONATE_NAME_KEY) || email };
  } catch {
    return null;
  }
}

export function isImpersonating(): boolean {
  return getImpersonation() !== null;
}

export function setImpersonation(email: string, name: string): void {
  try {
    window.sessionStorage.setItem(IMPERSONATE_EMAIL_KEY, email);
    window.sessionStorage.setItem(IMPERSONATE_NAME_KEY, name || email);
  } catch {
    /* ignore */
  }
  dispatchAuthChanged();
}

export function clearImpersonation(): void {
  try {
    window.sessionStorage.removeItem(IMPERSONATE_EMAIL_KEY);
    window.sessionStorage.removeItem(IMPERSONATE_NAME_KEY);
  } catch {
    /* ignore */
  }
  dispatchAuthChanged();
}

/**
 * Décode un segment base64url (JWT) en string. `atob` n'accepte pas les
 * caractères base64url (`-` `_`) ni l'absence de padding : les access tokens
 * Keycloak en contiennent fréquemment, d'où cette normalisation indispensable.
 */
function base64UrlDecode(segment: string): string {
  const base64 = segment.replace(/-/g, '+').replace(/_/g, '/');
  const padded = base64 + '='.repeat((4 - (base64.length % 4)) % 4);
  return atob(padded);
}

function decodeJWTPayload(): any | null {
  const jwt = getJWT();
  if (!jwt) return null;
  try {
    const payloadB64 = jwt.split('.')[1];
    return JSON.parse(base64UrlDecode(payloadB64));
  } catch (e) {
    return null;
  }
}

/**
 * Decodes the token (sessionStorage ou cookie) and returns user info.
 *
 * Supporte DEUX formats de jeton :
 *  - access token Keycloak (modèle cible) : claims `email` / `preferred_username` / `sub`.
 *  - ancien session_jwt interne (desktop/Teams, transition) : claim `upn` (= email).
 * Returns null if missing or invalid.
 */
export function getUserFromJWT(): JWTUser | null {
  // En mode impersonation, l'identité APPLICATIVE est celle de la cible : tous
  // les appels `/{user_id}/...` et les clés react-query basculent ainsi sans
  // toucher aux 15 composants qui consomment cette fonction. L'authentification
  // (token, expiration, calendar) continue de reposer sur le vrai JWT.
  const imp = getImpersonation();
  if (imp) {
    return { username: imp.email, name: imp.name, email: imp.email };
  }
  return getRealUserFromJWT();
}

/**
 * Identité RÉELLE de l'utilisateur (toujours dérivée du JWT, jamais impersonnée).
 * À utiliser quand on a besoin du compte authentifié indépendamment de
 * l'impersonation (ex: affichage "vous êtes X").
 */
export function getRealUserFromJWT(): JWTUser | null {
  const payload = decodeJWTPayload();
  if (!payload) return null;
  const email = payload.upn || payload.email || payload.preferred_username;
  return {
    username: email,
    name: payload.name || payload.preferred_username,
    email,
  };
}

/**
 * Checks if a valid token exists (sessionStorage ou cookie).
 * Tolère les deux formats : Keycloak (`email`/`preferred_username`/`sub`) et interne (`upn`).
 */
export function isAuthenticatedJWT(): boolean {
  const payload = decodeJWTPayload();
  return !!(payload && (payload.upn || payload.email || payload.preferred_username || payload.sub));
}

/**
 * Returns the value of `calendar_authorized` from the JWT payload, or null if no JWT / invalid.
 */
export function getCalendarAuthorizedFromJWT(): boolean | null {
  const payload = decodeJWTPayload();
  if (!payload) return null;
  if (typeof payload.calendar_authorized === 'boolean') {
    return payload.calendar_authorized;
  }
  return null;
}

/**
 * Returns the number of seconds until the JWT expires (from `exp` claim).
 * Returns null if no JWT, no `exp` claim, or invalid JWT.
 * A negative value means the JWT is already expired.
 */
export function getJWTSecondsUntilExpiry(): number | null {
  const payload = decodeJWTPayload();
  if (!payload || typeof payload.exp !== 'number') return null;
  const nowSec = Math.floor(Date.now() / 1000);
  return payload.exp - nowSec;
}
